import React, { useId, useState } from 'react';
import { Box, ButtonBase, SvgIcon, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// PR #684 review (M4) — this data comes from a different, optimistically-typed service
// (base_shay's approval_details.action_preview) with no runtime validation at the boundary. One
// malformed item (e.g. `fields` missing instead of []) previously threw during render and blanked
// the whole Approvals page/panel, since nothing here caught it. This local boundary degrades a bad
// payload to a small "preview unavailable" notice instead, same pattern as WorkflowErrorBoundary.
interface ErrorBoundaryProps {
  // PR #685 review (M4) — ApprovalPanel reuses one preview instance across approvals; without
  // this, a single malformed payload latched "Preview unavailable." for every later approval too,
  // since getDerivedStateFromError only ever turns hasError on, never off. Passing something that
  // changes per approval (its actions array) lets the boundary recover for the next one.
  resetKey: unknown;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ApprovalActionPreviewBoundary extends React.Component<React.PropsWithChildren<ErrorBoundaryProps>, ErrorBoundaryState> {
  constructor(props: React.PropsWithChildren<ErrorBoundaryProps>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    // PR #685 review (M4) — previously swallowed with no log at all, so a malformed payload left
    // no trace to debug from.
    console.error('ApprovalActionPreview failed to render:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Preview unavailable.
        </Typography>
      );
    }
    return this.props.children;
  }
}

/** One labelled, display-ready value (the backend formats it). */
export interface ApprovalActionField {
  label: string;
  value: string;
}

/**
 * One action an approval will run when approved. Shaped by base_shay's
 * `approval_details.action_preview` (services/approval_action_preview.py), so this
 * component needs no knowledge of individual tools or their argument names.
 */
export interface ApprovalActionPreviewItem {
  index: number;
  tool: string;
  title: string;
  fields: ApprovalActionField[];
}

interface ApprovalActionPreviewProps {
  actions?: ApprovalActionPreviewItem[] | null;
  /** Heading shown above the actions. */
  heading?: string;
  /**
   * Show a one-line summary (the action titles) with a down-arrow toggle instead of
   * the full details. For places where vertical space is tight, such as the approvals
   * list; the details stay one click away. Off by default: the side panel shows all.
   */
  collapsible?: boolean;
  /** Only used with `collapsible`: start with the details open. */
  defaultExpanded?: boolean;
}

// Values that are long or multi-line (e.g. vendor-bill line items) get a full-width row.
const isWide = (value: string) => value.includes('\n') || value.length > 80;

// base_shay marks a step it knows will not run with a field carrying this label. It is
// surfaced even while collapsed so a warning is never hidden behind the toggle.
const SKIP_WARNING_LABEL = 'Will be skipped';

// Drawn here rather than imported from @mui/icons-material: several test files mock
// that package down to a handful of icons, and this keeps the component independent.
const ChevronDownIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <SvgIcon
    fontSize="small"
    sx={{ flexShrink: 0, transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'none' }}
  >
    <path d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z" />
  </SvgIcon>
);

const headingSx = {
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'text.secondary',
} as const;

/**
 * Shows the approver exactly what approving will do: each deferred action and the
 * values it will run with. Renders nothing for approvals that carry no action
 * (e.g. a plain human sign-off), so it is safe to mount unconditionally.
 *
 * Values are rendered as plain text nodes (React escapes them) — they originate from
 * inbound email content, so they must never be interpreted as HTML or markdown.
 */
const ApprovalActionPreviewContent: React.FC<ApprovalActionPreviewProps> = ({
  actions,
  heading = "What you're approving",
  collapsible = false,
  defaultExpanded = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  // Hooks must run before the early return below so their order never changes.
  const headingId = useId();
  const contentId = useId();
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!actions || actions.length === 0) return null;

  const showDetails = !collapsible || expanded;
  const summary = actions
    .map((action, position) => (actions.length > 1 ? `${position + 1}. ${action.title}` : action.title))
    .join(' · ');
  // PR #684 review (M4) — `fields` is assumed to always be an array, but the data is typed
  // optimistic against a separate service's response; default to [] rather than letting a
  // missing/null `fields` throw in .some/.length/.map below.
  const skippedSteps = actions.filter((action) => (action.fields ?? []).some((f) => f.label === SKIP_WARNING_LABEL)).length;

  return (
    <Box
      data-testid="approval-action-preview"
      // A named landmark, so screen-reader users can jump to it before the buttons.
      role="region"
      aria-labelledby={headingId}
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f8f9fa',
      }}
    >
      {collapsible ? (
        <ButtonBase
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={expanded ? contentId : undefined}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
            textAlign: 'left',
            borderRadius: 0.5,
            mb: expanded ? 1 : 0,
          }}
        >
          <Typography id={headingId} variant="caption" sx={{ ...headingSx, flexShrink: 0 }}>
            {heading}
          </Typography>
          {expanded ? (
            <Box sx={{ flex: 1 }} />
          ) : (
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {summary}
            </Typography>
          )}
          {!expanded && skippedSteps > 0 && (
            <Typography variant="caption" sx={{ flexShrink: 0, fontWeight: 600, color: 'warning.main' }}>
              {skippedSteps === 1 ? '1 step will be skipped' : `${skippedSteps} steps will be skipped`}
            </Typography>
          )}
          <Typography variant="caption" sx={{ flexShrink: 0, color: 'text.secondary' }}>
            {expanded ? 'Hide details' : 'Show details'}
          </Typography>
          <ChevronDownIcon expanded={expanded} />
        </ButtonBase>
      ) : (
        <Typography id={headingId} variant="caption" sx={{ ...headingSx, display: 'block', mb: 1 }}>
          {heading}
        </Typography>
      )}

      {showDetails && (
        <Box id={contentId}>
          {actions.map((action, position) => {
            const fields = action.fields ?? [];
            return (
            <Box
              key={`${action.index}-${action.tool}`}
              data-testid="approval-action"
              sx={{
                ...(position > 0 && { mt: 1.5, pt: 1.5, borderTop: `1px dashed ${theme.palette.divider}` }),
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: fields.length ? 0.75 : 0 }}>
                {actions.length > 1 ? `${position + 1}. ${action.title}` : action.title}
              </Typography>

              {fields.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: 1,
                  }}
                >
                  {fields.map((field, fieldPosition) => (
                    <Box
                      // Not just the label: an unlabelled arg is shown under its raw key, which can
                      // coincide with a friendly label ("Email"), and React keys must be unique.
                      key={`${field.label}-${fieldPosition}`}
                      sx={{ minWidth: 0, ...(isWide(field.value) && { gridColumn: '1 / -1' }) }}
                    >
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.3 }}>
                        {field.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.4 }}
                      >
                        {field.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const ApprovalActionPreview: React.FC<ApprovalActionPreviewProps> = (props) => (
  <ApprovalActionPreviewBoundary resetKey={props.actions}>
    <ApprovalActionPreviewContent {...props} />
  </ApprovalActionPreviewBoundary>
);

export default ApprovalActionPreview;
